import useQuickJs from '@/composables/quickjs'
import { assocPath, isEmpty, isNil, path } from 'ramda'
import { computed, reactive, ref, shallowRef, toRaw, watch } from 'vue'
import type { CPInputConfiguration, ExtendedBlock, ListView, SelectionList } from 'widget-components'
import { getBlockValueForConditions } from '../../components/block-map'
import type { QuickJSContext } from 'quickjs-emscripten'
import { flattenBlocks, replaceNestedIndexKey, type FlattenBlock, type indexStack } from './utils'
import { blocks } from 'widget-components/integrations/CustomPage/blocks-state'
import { useEventBus } from '@vueuse/core'
import { captureException } from '@sentry/vue'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BlockValuesMap = Record<string, any>

type GetConditionValue = (id: string, parentIndex?: number, parentId?: string) => boolean;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RepeatableContainerValues = Record<string, any>

const getFieldName = (fieldName: string) => fieldName.includes('.')
    ? fieldName.split('.').pop() || ''
    : fieldName

export default function useConditionsManager(
    errorCallback?: (error: unknown) => void,
) {
    const initialized = ref(false)
    const displayConditions = shallowRef<Record<string, string>>({})
    const disableConditions = shallowRef<Record<string, string>>({})
    const displayConditionsEnabled = computed(() => !isEmpty(displayConditions.value))
    const disableConditionsEnabled = computed(() => !isEmpty(disableConditions.value))
    const displayConditionsResults = reactive<Record<string, boolean>>({})
    const disableConditionsResults = reactive<Record<string, boolean>>({})
    const paused = ref(false)

    const {
        vms,
        isReady: vmsIsReady,
        setDataInContext,
        runEvalCode,
        getPropValue,
        load,
    } = useQuickJs()

    const flatBlocks = computed(() => {
        const firstBlock = blocks.value[0]

        return firstBlock?.type === 'CONTAINER'
            ? flattenBlocks(firstBlock?.children as ExtendedBlock[]) as FlattenBlock[]
            : blocks.value as ExtendedBlock[]
    })

    const blockValuesMapByFieldName = computed(() => {
        const result: BlockValuesMap = {}

        const collectValues = (
            blocks: FlattenBlock[],
            parentContainer?: RepeatableContainerValues,
        ) => {
            for (const block of blocks) {
                if (block.type === 'REPEATABLE_CONTAINER') {
                    const fieldName = (block as ExtendedBlock<CPInputConfiguration>).fieldName
                    if (!fieldName) continue

                    const iterations = block.children ?? []
                    const iterationValues: RepeatableContainerValues[] = []

                    iterations.forEach((iterationGroup) => {
                        const iterationResult: RepeatableContainerValues = {}
                        collectValues(iterationGroup, iterationResult)
                        iterationValues.push(iterationResult)
                    })

                    const simpleName = getFieldName(fieldName)
                    if (parentContainer) {
                        parentContainer[simpleName] = iterationValues
                    } else {
                        result[simpleName] = iterationValues
                    }

                    continue
                }

                // Standard block with fieldName
                const fieldName = (block as ExtendedBlock<CPInputConfiguration>).fieldName
                if (fieldName) {
                    const value = getBlockValueForConditions(block as ExtendedBlock<CPInputConfiguration>)
                    const simpleName = getFieldName(fieldName)
                    if (parentContainer) {
                        parentContainer[simpleName] = value
                    } else {
                        result[simpleName] = value
                    }
                }
            }
        }

        collectValues(flatBlocks.value as FlattenBlock[])

        return result
    })

    const assignConditions = () => {
        displayConditions.value = mapConditions('displayCondition')
        disableConditions.value = mapConditions('disableCondition')
    }

    const init = async () => {
        assignConditions()

        if (displayConditionsEnabled.value || disableConditionsEnabled.value) {
            await load()

            watch(
                blockValuesMapByFieldName,
                runComponentsConditions,
                {
                    deep: true,
                    immediate: true,
                },
            )
        } else {
            initialized.value = true
        }
    }

    // It's easier to eval conditions in QuickJS when they are all mapped into flat object
    const mapConditions = (key: 'disableCondition' | 'displayCondition') => {
        const firstBlock = blocks.value[0]

        const blocksToMap = firstBlock?.type === 'CONTAINER'
            ? flattenBlocks(firstBlock?.children as ExtendedBlock[])
            : blocks.value

        let result: Record<string, string> = {}

        const processBlocks = (
            blocks: ExtendedBlock[] | FlattenBlock[],
            _indexStack: indexStack = [],
        ) => {
            for (const block of blocks) {
                let condition = block.display?.[key]
                if (condition) {
                    if (condition.includes('$$index')) {
                        condition = replaceNestedIndexKey(condition, _indexStack)
                    }
                    result = assocPath([block.id], condition, result)
                }

                if (block.type === 'LIST_VIEW') {
                    const listView = block as ExtendedBlock<ListView>
                    listView.items.forEach((item, index) => {
                        item.components.forEach((component) => {
                            let displayConditionForComponent = component.display?.[key] || ''
                            if (displayConditionForComponent.includes('$$index')) {
                                displayConditionForComponent = displayConditionForComponent.replace(/\$\$index/g, String(index))
                            }
                            if (displayConditionForComponent) {
                                result = assocPath([`${listView.id}_${index}_${component.id}`], displayConditionForComponent, result)
                            }
                        })
                    })
                }

                if (block.type === 'REPEATABLE_CONTAINER') {
                    const repeatable = block as FlattenBlock
                    const childrenGroups = repeatable.children ?? []

                    childrenGroups.forEach((childGroup: FlattenBlock[], index) => {
                        const indexStack = [..._indexStack]

                        indexStack.push({
                            index,
                            fieldName: getFieldName((repeatable as CPInputConfiguration).fieldName),
                        })

                        processBlocks(childGroup, indexStack)
                    })
                }

                if (block.type === 'SELECTION_LIST') {
                    const selectionList = block as SelectionList

                    selectionList.items.forEach(i => {
                        const conditionValue = i.display?.[key] || ''
                        if (conditionValue) {
                            result = assocPath([i.id!], conditionValue, result)
                        }
                    })
                }
            }
        }

        processBlocks(blocksToMap)

        return result
    }

    const handleDisplayConditions = (vms: QuickJSContext) => {
        const displayResults = vms.newObject()

        runEvalCode(vms, displayConditions.value, displayResults)

        if (displayResults) {
            vms.setProp(vms.global, 'displayResults', displayResults)
        }

        const newDisplayResults = getPropValue(vms, 'displayResults') as Record<string, boolean>

        Object.assign(displayConditionsResults, newDisplayResults)
    }

    const handleDisableConditions = (vms: QuickJSContext) => {
        const disabledResults = vms.newObject()

        runEvalCode(vms, disableConditions.value, disabledResults)

        if (disabledResults) {
            vms.setProp(vms.global, 'disabledResults', disabledResults)
        }

        Object.assign(disableConditionsResults, getPropValue(vms, 'disabledResults'))
    }

    const runComponentsConditions = () => {
        if (
            paused.value ||
            !vmsIsReady.value
            && (!displayConditionsEnabled.value || !disableConditionsEnabled.value)
        ) return

        const _vms = toRaw(vms.value) as QuickJSContext

        if (!_vms) return

        setDataInContext(_vms, blockValuesMapByFieldName.value)

        try {
            if (displayConditionsEnabled.value) {
                handleDisplayConditions(_vms)
            }

            if (disableConditionsEnabled.value) {
                handleDisableConditions(_vms)
            }
        } catch (e) {
            captureException(e)
            errorCallback?.(e)
            console.error(e) // eslint-disable-line no-console
        }

        if (!initialized.value) {
            initialized.value = true
        }
    }

    const getConditionPathVal = (id: string, parentIndex?: number, parentId?: string) => {
        const conditionPath = [id]

        if (!isNil(parentIndex)) {
            conditionPath.unshift(String(parentIndex))
        }

        if (!isNil(parentId)) {
            conditionPath.unshift(parentId)
        }

        return conditionPath.join('_')
    }

    const getDisableValue: GetConditionValue = (id, parentIndex, parentId): boolean => {
        const conditionPathVal = getConditionPathVal(id, parentIndex, parentId)

        return path([conditionPathVal], disableConditions.value) ? !!path([conditionPathVal], disableConditionsResults) : false
    }

    const getDisplayValue: GetConditionValue = (id, parentIndex, parentId): boolean => {
        const conditionPathVal = getConditionPathVal(id, parentIndex, parentId)

        return path([conditionPathVal], displayConditions.value) ? !!path([conditionPathVal], displayConditionsResults) : true
    }

    useEventBus('block-component-changed').on(() => {
        assignConditions()
        paused.value = false
        runComponentsConditions()
    })

    useEventBus('block-component-change-start').on(() => {
        paused.value = true
    })

    return {
        displayConditions,
        disableConditions,
        displayConditionsEnabled,
        disableConditionsEnabled,
        displayConditionsResults,
        disableConditionsResults,
        blockValuesMapByFieldName,
        getDisableValue,
        getDisplayValue,
        init,
        initialized,
        assignConditions,
        flatBlocks,
    }
}
